import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/config";
import { Plus, Edit2, Trash2, X, Save, Upload } from "lucide-react";

const EMPTY = {
  name: "", role: "", bio: "", expertise: "", certifications: "", languages: "",
  photo: "", photo_position: "50% 50%", color: "#1C1580", linkedin: "", order: 0, published: true,
};

const COLORS = [
  { v: "#1C1580", label: "Indigo" },
  { v: "#E62C86", label: "Rose" },
  { v: "#F3A114", label: "Orange" },
  { v: "#EEE316", label: "Jaune" },
  { v: "#9DBC1D", label: "Vert anis" },
  { v: "#52A385", label: "Vert eau" },
];

function formatErr(d) {
  if (!d) return "Erreur";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function AdminTeam() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/team").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const onEdit = (m) => {
    setEditing(m.id);
    setForm({
      ...EMPTY,
      ...m,
      expertise: (m.expertise || []).join(", "),
      certifications: (m.certifications || []).join("\n"),
      languages: (m.languages || []).join(", "),
    });
    setErr("");
  };

  const onNew = () => { setEditing("new"); setForm(EMPTY); setErr(""); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = data.url.startsWith("http") ? data.url : `${API.replace(/\/api$/, "")}${data.url}`;
      setForm(f => ({ ...f, photo: fullUrl }));
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSave = async () => {
    setErr("");
    const payload = {
      ...form,
      expertise: form.expertise.split(",").map(s => s.trim()).filter(Boolean),
      certifications: form.certifications.split("\n").map(s => s.trim()).filter(Boolean),
      languages: form.languages.split(",").map(s => s.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") await api.post("/admin/team", payload);
      else await api.put(`/admin/team/${editing}`, payload);
      setEditing(null); load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ce membre ?")) return;
    await api.delete(`/admin/team/${id}`); load();
  };

  const focalPos = form.photo_position || "50% 50%";
  const [px, py] = focalPos.split(" ");

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <p className="section-num">Équipe</p>
          <h1 className="display text-4xl mt-3">Membres de l'équipe</h1>
        </div>
        <button onClick={onNew} className="btn-pill btn-primary" data-testid="admin-new-team">
          <Plus size={16}/> Nouveau membre
        </button>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(m => (
          <div key={m.id} className="bg-paper border border-ink/10 relative" data-testid={`admin-team-${m.id}`}>
            <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: m.color }}/>
            <div className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-paper-2 overflow-hidden flex items-center justify-center" style={{ background: m.photo ? "transparent" : m.color }}>
                  {m.photo
                    ? <img src={m.photo} alt={m.name} className="w-full h-full object-cover" style={{ objectPosition: m.photo_position || "50% 50%" }}/>
                    : <span className="display text-white text-base">{m.name.split(' ').map(s=>s[0]).slice(0,2).join('')}</span>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="display text-lg leading-tight truncate">{m.name}</p>
                  <p className="text-[12px] text-ink/60 truncate">{m.role}</p>
                </div>
              </div>
              <p className="text-[13px] text-ink/70 leading-relaxed mt-4 line-clamp-3">{m.bio}</p>
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-ink/10">
                <span className={`text-[10px] uppercase tracking-wider ${m.published ? "text-accent-lime" : "text-ink/40"}`}>
                  {m.published ? "Publié" : "Brouillon"} · Ordre {m.order}
                </span>
                <div className="flex gap-1">
                  <button onClick={() => onEdit(m)} className="p-2 hover:bg-ink/5"><Edit2 size={14}/></button>
                  <button onClick={() => onDelete(m.id)} className="p-2 hover:bg-paper-2 text-accent-pink"><Trash2 size={14}/></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="bg-paper max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-8 border-b border-ink/10 flex items-center justify-between">
              <h2 className="display text-2xl">{editing === "new" ? "Nouveau membre" : "Modifier le membre"}</h2>
              <button onClick={() => setEditing(null)}><X size={22}/></button>
            </div>
            <div className="p-8 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div><label className="text-xs uppercase tracking-wider text-ink/60">Nom complet</label><input className="field" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}/></div>
                <div><label className="text-xs uppercase tracking-wider text-ink/60">Rôle / Titre</label><input className="field" value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}/></div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Bio (1 paragraphe)</label>
                <textarea className="field" rows={5} value={form.bio} onChange={e => setForm(f => ({...f, bio: e.target.value}))}/>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Photo</label>
                {form.photo && (
                  <div className="mt-3 space-y-4">
                    <p className="text-[12px] text-ink/70 leading-relaxed">
                      <strong>Cliquez sur la photo pour définir le point focal</strong> (zone qui restera centrée
                      quand l'image est recadrée sur le site public).
                    </p>
                    <div className="flex gap-6 flex-wrap items-start">
                      {/* Picker */}
                      <div
                        className="relative cursor-crosshair select-none"
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect();
                          const x = Math.max(0, Math.min(100, Math.round(((e.clientX - r.left) / r.width) * 100)));
                          const y = Math.max(0, Math.min(100, Math.round(((e.clientY - r.top) / r.height) * 100)));
                          setForm(f => ({ ...f, photo_position: `${x}% ${y}%` }));
                        }}
                        data-testid="photo-focal-picker"
                      >
                        <img src={form.photo} alt="" className="block max-h-72 max-w-md border border-ink/10"/>
                        <div
                          className="absolute w-7 h-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-accent-pink/85 shadow-lg pointer-events-none flex items-center justify-center"
                          style={{ left: px, top: py }}
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-white"/>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setForm(f => ({ ...f, photo: "", photo_position: "50% 50%" })); }}
                          className="absolute top-2 right-2 p-1.5 bg-paper rounded-full shadow border border-ink/10"
                        >
                          <X size={14}/>
                        </button>
                      </div>
                      {/* Live preview */}
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-ink/60 mb-2">Aperçu sur le site</p>
                        <div className="w-56 h-72 bg-brand-deep border border-ink/10 overflow-hidden">
                          <img
                            src={form.photo}
                            alt=""
                            className="w-full h-full object-cover"
                            style={{ objectPosition: focalPos }}
                          />
                        </div>
                        <p className="text-[11px] uppercase tracking-wider text-ink/60 mt-3">Position : <span className="text-brand font-bold">{focalPos}</span></p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap pt-2">
                      <span className="text-[11px] uppercase tracking-wider text-ink/60">Préréglages :</span>
                      {[
                        { label: "Haut", pos: "50% 0%" },
                        { label: "Visage", pos: "50% 20%" },
                        { label: "Centre", pos: "50% 50%" },
                        { label: "Bas", pos: "50% 100%" },
                      ].map(p => (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => setForm(f => ({ ...f, photo_position: p.pos }))}
                          className={`text-[11px] px-3 py-1 rounded-full border transition-colors ${focalPos === p.pos ? "bg-brand text-white border-brand" : "border-ink/20 hover:border-brand"}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-3 mt-3 items-center">
                  <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden"/>
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-pill btn-ghost disabled:opacity-60">
                    {uploading ? "Envoi…" : (<><Upload size={14}/> {form.photo ? "Remplacer la photo" : "Uploader une photo"}</>)}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Expertise (séparé par des virgules)</label>
                <input className="field" value={form.expertise} onChange={e => setForm(f => ({...f, expertise: e.target.value}))}/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Certifications (une par ligne)</label>
                <textarea className="field" rows={4} value={form.certifications} onChange={e => setForm(f => ({...f, certifications: e.target.value}))}/>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div><label className="text-xs uppercase tracking-wider text-ink/60">Langues (virgules)</label><input className="field" value={form.languages} onChange={e => setForm(f => ({...f, languages: e.target.value}))}/></div>
                <div><label className="text-xs uppercase tracking-wider text-ink/60">LinkedIn (URL)</label><input className="field" value={form.linkedin} onChange={e => setForm(f => ({...f, linkedin: e.target.value}))}/></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-wider text-ink/60">Couleur</label>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {COLORS.map(c => (
                      <button key={c.v} type="button" onClick={() => setForm(f => ({...f, color: c.v}))}
                        className={`w-9 h-9 rounded-full border-2 transition-all ${form.color === c.v ? "border-ink scale-110" : "border-transparent"}`}
                        style={{ background: c.v }} title={c.label}/>
                    ))}
                  </div>
                </div>
                <div><label className="text-xs uppercase tracking-wider text-ink/60">Ordre d'affichage</label><input type="number" className="field" value={form.order} onChange={e => setForm(f => ({...f, order: parseInt(e.target.value) || 0}))}/></div>
              </div>
              <label className="flex items-center gap-3 pt-3">
                <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({...f, published: e.target.checked}))}/>
                <span className="text-sm">Publié (visible sur le site)</span>
              </label>
              {err && <p className="text-accent-pink text-sm">{err}</p>}
              <div className="flex gap-3 pt-4">
                <button onClick={onSave} className="btn-pill btn-primary"><Save size={16}/> Enregistrer</button>
                <button onClick={() => setEditing(null)} className="btn-pill btn-ghost">Annuler</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
